import {
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  type ReactNode,
} from "react";
import {
  Download,
  FileText,
  Paperclip,
  Plus,
  RotateCcw,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import type { ConversationAttachment } from "@playbookai/player-ai-client";
import {
  FAILED_DOCUMENT_RETRIEVAL_STATUSES,
  TRANSIENT_DOCUMENT_RETRIEVAL_STATUSES,
} from "@playbookai/api-clients";
import {
  Button,
  CountBadge,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Tooltip,
  TruncatedText,
} from "@playbookai/ui";

/** Statuses where the bytes are still moving, so the file is not usable yet. */
export const UPLOADING_ATTACHMENT_STATUSES: ReadonlySet<string> = new Set([
  "creating",
  "uploading",
]);

/** The files dialog's height on tall windows (px): its size before PB-5381. */
const FILES_DIALOG_MAX_HEIGHT = 360;

export interface ConversationFilesPopoverProps {
  /** Every file the active-attachment query currently returns for this conversation. */
  readonly attachments: readonly ConversationAttachment[];
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** False only when there is no conversation to hold files at all. */
  readonly hasConversation: boolean;
  /** False for a read-only viewer: they may still open the list and download. */
  readonly canMutate: boolean;
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly busyId: string | null;
  readonly retryingAttachmentId: string | null;
  /**
   * PB-5380: set while the next message already holds as many files as one
   * message may. Add file is then disabled, and this sentence is shown as its
   * visible, linked reason — a disabled button leaves the tab order, so a
   * tooltip alone would never be read.
   */
  readonly addFileDisabledReason?: string | null | undefined;
  readonly onAddFile: () => void;
  readonly onCancelUpload: (attachmentId: string) => void;
  readonly onDownload: (attachmentId: string) => void;
  readonly onRetryRetrieval: (attachmentId: string) => void;
  readonly onRemove: (attachmentId: string) => void;
  readonly onDeleteEverywhere: (attachment: ConversationAttachment) => void;
}

export function AttachmentRowShell({
  attachment,
  actions,
  children,
  className = "bg-pb-card-elevated",
}: {
  readonly attachment: ConversationAttachment;
  readonly actions?: ReactNode;
  readonly children?: ReactNode;
  readonly className?: string;
}): ReactNode {
  const filename = attachmentFilename(attachment);
  /* Kept a plain row: ListItem is one button, and this row holds its own
     actions. */
  return (
    <li
      className={`rounded-md border border-pb-border px-2 py-1.5 ${className}`}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        <FileText
          className="h-3.5 w-3.5 shrink-0 text-pb-text-muted"
          aria-hidden
        />
        {/* A clipped name shows in the design-system Tooltip (d1), not a
            native title. */}
        <span className="min-w-0 flex-1 text-dense-1 font-medium text-pb-text">
          <TruncatedText>{filename}</TruncatedText>
        </span>
        {/* The Spinner ring in place of the LoaderCircle glyph (OK-38), in
            today's muted ink. */}
        {attachment.status === "checking" ? (
          <span className="flex text-pb-text-muted">
            <Spinner size="sm" aria-label={`Checking file: ${filename}`} />
          </span>
        ) : null}
        {attachment.status === "ready" ? (
          <ShieldCheck
            className="h-3.5 w-3.5 text-pb-green-text"
            aria-label={`Ready: ${filename}`}
          />
        ) : null}
        {actions}
      </div>
      {children}
    </li>
  );
}

/**
 * The conversation's persistent file list, as a dialog popover beside the
 * composer. Distinct from the composer's own metadata strip, which shows only
 * the files selected for the NEXT message: those disappear once that message
 * sends, while everything here stays for as long as it is linked.
 *
 * Data-blind by design — the owning hook holds the query and every mutation.
 */
export function ConversationFilesPopover({
  attachments,
  open,
  onOpenChange,
  hasConversation,
  canMutate,
  isLoading,
  error,
  busyId,
  retryingAttachmentId,
  addFileDisabledReason,
  onAddFile,
  onCancelUpload,
  onDownload,
  onRetryRetrieval,
  onRemove,
  onDeleteEverywhere,
}: ConversationFilesPopoverProps): ReactNode {
  const headingId = useId();
  const addFileReasonId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const wasOpenRef = useRef(false);

  /* A `contentRole="dialog"` popover gets NO focus handling from the library —
     the roving-focus contract is menuitem-only — so the panel must take focus
     itself. Focus lands on the panel rather than its first control because a
     read-only or empty panel may have no control at all.

     A LAYOUT effect, not a passive one, for the close half: opening a Modal
     from here closes this popover in the same commit, and ModalShell captures
     `previouslyFocused` in a passive effect. Restoring the trigger first is
     what makes the modal hand focus back to it on close. */
  useLayoutEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      panelRef.current?.focus();
    } else if (wasOpenRef.current) {
      wasOpenRef.current = false;
      triggerRef.current?.focus();
    }
  }, [open]);

  /* Every modal transition goes through here: close first, then open. Leaving
     the popover mounted under a Modal would put its content above the scrim
     (z-popover > z-overlay) while the modal's document-level Tab handler drags
     focus out of it. */
  const closeThen = useCallback(
    (action: () => void): void => {
      onOpenChange(false);
      action();
    },
    [onOpenChange],
  );

  const count = attachments.length;

  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      align="start"
      contentRole="dialog"
    >
      {/* PB-5381: one 28px icon control, so the text box keeps its width. The
          badge counts the CONVERSATION's files (the next message's own count
          is the "N of 10" line above the input); the design-system tooltip
          says so for a sighted reader (d1), and the name carries the full
          count for everyone, so the tooltip does not describe it again. */}
      <Tooltip content={`Conversation files (${count})`} describeChild={false}>
        <PopoverTrigger>
          <Button
            ref={triggerRef}
            variant="ghost"
            size="sm"
            iconOnly
            aria-label={`Conversation files (${count})`}
            disabled={!hasConversation}
            className="relative"
          >
            <Paperclip className="h-4 w-4 shrink-0" aria-hidden />
            <CountBadge count={count} tone="neutral" ring="card" />
          </Button>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent
        aria-labelledby={headingId}
        sideOffset={12}
        // The dialog is the ONE area that scrolls (PB-5381): the positioner
        // caps it here on tall windows and at the room it has on short ones,
        // and the list never scrolls on its own. It stays in block flow on
        // purpose: the positioner measures scrollHeight, which must stay the
        // content's full height while a cap applies, or it clamps and unclamps
        // on every scroll.
        maxHeight={FILES_DIALOG_MAX_HEIGHT}
        // The md padding step is today's p-3. The corner, strong border and
        // card fill stay as they are: neither surface (default, rail) paints
        // them, and no PR 3 look row approves the change.
        padding="md"
        className="w-80 max-w-[92vw] rounded-lg border-pb-border-strong bg-pb-card"
      >
        {/* The programmatic focus target for a dialog popover the library does
            not focus for us; -1 keeps it out of the tab ring. */}
        <div ref={panelRef} tabIndex={-1} className="focus-ring">
          {/* Pinned while the dialog scrolls, so Add file stays in reach. A
              child of the whole panel, since a sticky row cannot leave its
              parent. It takes the dialog's top padding as its own, so it looks
              the same pinned and at rest.

              Task 16 — it also takes the dialog's TOP CORNERS. The row's
              opaque fill reaches the panel's padding edge, and while the list
              fits (no max height, so the panel is not a clipping scroll box)
              nothing clipped its square corners: they painted over the
              rounded border, clipping both top corners and breaking the top
              border. Rounding it with the panel's own `rounded-lg` keeps the
              fill inside the curve (keep the two in step). */}
          <div className="sticky -top-3 z-raised -mx-3 -mt-3 flex items-center justify-between gap-2 rounded-t-lg bg-pb-card px-3 pt-3 pb-1">
            <div id={headingId} className="text-sm font-medium text-pb-text">
              Conversation files
            </div>
            {canMutate ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="shrink-0 whitespace-nowrap"
                disabled={Boolean(addFileDisabledReason)}
                aria-describedby={
                  addFileDisabledReason ? addFileReasonId : undefined
                }
                onClick={() => closeThen(onAddFile)}
              >
                <Plus className="h-3.5 w-3.5" aria-hidden />
                Add file
              </Button>
            ) : null}
          </div>
          <div className="mb-2">
            <div className="text-dense-2 text-pb-text-muted">
              Stay available to Player AI for the whole conversation.
            </div>
            {canMutate && addFileDisabledReason ? (
              <p
                id={addFileReasonId}
                className="mt-1 text-dense-2 text-pb-text-muted"
              >
                {addFileDisabledReason}
              </p>
            ) : null}
          </div>

          {/* Kept hand-built, the notice and the two lines below: Alert
              paints a glyph, a 40% border and px-3 py-2.5, EmptyState size="sm"
              drops the dashed frame, and a Spinner beside "Loading files…" is
              not on a PR 3 look row. */}
          {error ? (
            <p className="mb-2 rounded-md border border-pb-red/30 bg-pb-red-soft px-2 py-1.5 text-dense-1 text-pb-red-text">
              {error}
            </p>
          ) : null}

          {isLoading && count === 0 ? (
            <p className="text-dense-1 text-pb-text-muted">Loading files…</p>
          ) : count === 0 ? (
            <p className="rounded border border-dashed border-pb-border px-3 py-2 text-dense-1 text-pb-text-muted">
              No files in this conversation yet.
            </p>
          ) : (
            <ul className="space-y-1">
              {attachments.map((attachment) => (
                <ConversationFileRow
                  key={attachment.id}
                  attachment={attachment}
                  canMutate={canMutate}
                  busy={busyId === attachment.id}
                  retrying={retryingAttachmentId === attachment.id}
                  retryBlocked={
                    retryingAttachmentId !== null &&
                    retryingAttachmentId !== attachment.id
                  }
                  onCancelUpload={onCancelUpload}
                  onDownload={onDownload}
                  onRetryRetrieval={onRetryRetrieval}
                  onRemove={onRemove}
                  onDeleteEverywhere={() =>
                    closeThen(() => onDeleteEverywhere(attachment))
                  }
                />
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ConversationFileRow({
  attachment,
  canMutate,
  busy,
  retrying,
  retryBlocked,
  onCancelUpload,
  onDownload,
  onRetryRetrieval,
  onRemove,
  onDeleteEverywhere,
}: {
  readonly attachment: ConversationAttachment;
  readonly canMutate: boolean;
  readonly busy: boolean;
  readonly retrying: boolean;
  readonly retryBlocked: boolean;
  readonly onCancelUpload: (attachmentId: string) => void;
  readonly onDownload: (attachmentId: string) => void;
  readonly onRetryRetrieval: (attachmentId: string) => void;
  readonly onRemove: (attachmentId: string) => void;
  readonly onDeleteEverywhere: () => void;
}): ReactNode {
  const filename = attachmentFilename(attachment);
  const uploading = UPLOADING_ATTACHMENT_STATUSES.has(attachment.status);
  const disabled = busy || retrying;

  return (
    <AttachmentRowShell
      attachment={attachment}
      actions={
        <>
          {uploading && canMutate ? (
            <RowAction
              label={`Cancel upload: ${filename}`}
              disabled={disabled}
              onClick={() => onCancelUpload(attachment.id)}
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </RowAction>
          ) : null}
          {attachment.status === "ready" ? (
            <RowAction
              label={`Download: ${filename}`}
              disabled={disabled}
              onClick={() => onDownload(attachment.id)}
            >
              <Download className="h-3.5 w-3.5" aria-hidden />
            </RowAction>
          ) : null}
          {!uploading && canMutate ? (
            <>
              <RowAction
                label={`Remove from conversation: ${filename}`}
                disabled={disabled}
                onClick={() => onRemove(attachment.id)}
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </RowAction>
              <RowAction
                label={`Delete file everywhere: ${filename}`}
                disabled={disabled}
                destructive
                onClick={onDeleteEverywhere}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </RowAction>
            </>
          ) : null}
        </>
      }
    >
      {uploading ? (
        <p className="mt-0.5 text-dense-2 text-pb-text-muted">Uploading…</p>
      ) : attachment.status === "checking" ? (
        <p className="mt-0.5 text-dense-2 text-pb-text-muted">Checking file…</p>
      ) : attachment.status === "ready" ? (
        <AttachmentRetrievalLine
          attachment={attachment}
          canRetry={canMutate}
          retryDisabled={retryBlocked}
          retrying={retrying}
          onRetry={() => onRetryRetrieval(attachment.id)}
        />
      ) : (
        /* Kept as today's red line: Alert variant="inline" is 11px with a
           glyph, a look change no PR 3 row approves. */
        <p className="mt-0.5 text-dense-2 text-pb-red-text">
          {attachmentFailure(attachment)}
        </p>
      )}
    </AttachmentRowShell>
  );
}

function RowAction({
  label,
  disabled,
  destructive,
  onClick,
  children,
}: {
  readonly label: string;
  readonly disabled: boolean;
  readonly destructive?: boolean;
  readonly onClick: () => void;
  readonly children: ReactNode;
}): ReactNode {
  return (
    // eslint-disable-next-line ds/no-hand-built -- kept: Button ghost iconOnly xs is a 24px box painting the glyph text-pb-text-secondary (today a 22px muted box, dimmed to 40%); no PR 3 look row approves that.
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={
        destructive
          ? "rounded-md p-1 text-pb-red-text transition-colors hover:bg-pb-red-soft hover:text-pb-red-text active:bg-pb-red-soft focus-ring disabled:opacity-40"
          : "rounded-md p-1 text-pb-text-muted transition-colors hover:pb-overlay-paint hover:text-pb-text active:pb-overlay-paint-strong focus-ring disabled:opacity-40"
      }
    >
      {children}
    </button>
  );
}

export function attachmentFilename(attachment: ConversationAttachment): string {
  return attachment.display_filename ?? "Untitled document";
}

/**
 * The retrieval half of a file's lifecycle, told truthfully and separately from
 * the upload half: retrieval still processing — or terminally failed — never
 * blocks a send, it only limits what Player AI can search.
 */
export function AttachmentRetrievalLine({
  attachment,
  canRetry,
  retryDisabled,
  retrying,
  onRetry,
}: {
  readonly attachment: ConversationAttachment;
  readonly canRetry: boolean;
  readonly retryDisabled: boolean;
  readonly retrying: boolean;
  readonly onRetry: () => void;
}): ReactNode {
  const retrieval = attachment.retrieval;
  if (!retrieval) {
    return (
      <p className="mt-0.5 text-dense-2 text-pb-text-muted">
        File saved to this conversation. Player AI can read it once it&apos;s
        Ready.
      </p>
    );
  }
  if (TRANSIENT_DOCUMENT_RETRIEVAL_STATUSES.has(retrieval.status)) {
    return (
      <p className="mt-0.5 flex items-center gap-1 text-dense-2 text-pb-text-muted">
        {/* The Spinner ring in place of the LoaderCircle glyph (OK-38). */}
        <Spinner size="xs" aria-hidden />
        Preparing document for search…
      </p>
    );
  }
  if (retrieval.status === "ready") {
    return (
      <p className="mt-0.5 text-dense-2 text-pb-text-muted">
        Ready for Player AI.
      </p>
    );
  }
  if (!FAILED_DOCUMENT_RETRIEVAL_STATUSES.has(retrieval.status)) return null;
  if (retrieval.failure_code === "UNSUPPORTED_RETRIEVAL_FORMAT") {
    return (
      <p className="mt-0.5 text-dense-2 text-pb-text-muted">
        This file type can&apos;t be searched.
      </p>
    );
  }
  /* Kept as today's red line: Alert variant="inline" is 11px with a glyph,
     a look change no PR 3 row approves. */
  return (
    <div className="mt-0.5 flex flex-wrap items-center justify-between gap-1 text-dense-2 text-pb-red-text">
      <span>Retrieval failed. The original file is still available.</span>
      {canRetry && retrieval.retryable ? (
        <Button
          size="sm"
          variant="secondary"
          aria-label={`Retry retrieval: ${attachmentFilename(attachment)}`}
          disabled={retryDisabled}
          loading={retrying}
          onClick={onRetry}
        >
          <RotateCcw className="h-3 w-3" aria-hidden />
          Retry
        </Button>
      ) : null}
    </div>
  );
}

export function attachmentFailure(attachment: ConversationAttachment): string {
  if (attachment.status === "blocked") {
    if (attachment.failure_code === "SCAN_BLOCKED") {
      return "This file was blocked by the security check.";
    }
    if (attachment.failure_code === "SIGNATURE_MISMATCH") {
      return "The file contents do not match its type.";
    }
    if (attachment.failure_code === "CORRUPT_CONTAINER") {
      return "This Office file is damaged or has an unsafe structure.";
    }
    if (attachment.failure_code === "BINARY_TEXT") {
      return "This text file contains unsupported binary data.";
    }
    return "This file was blocked during validation.";
  }
  if (attachment.failure_code === "UPLOAD_ABORTED") return "Upload cancelled.";
  if (attachment.status === "invalidated" || attachment.status === "deleted") {
    return "This file is no longer available.";
  }
  return "The file could not be prepared. Remove it and try again.";
}
